import React, { useEffect, useState } from "react";
import Spinner from "react-bootstrap/Spinner";
import { Row, Col, Modal, Button, Form } from "react-bootstrap";
import Card from "../../../components/Card";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { EtsyAPI } from "../../../js/api";
import { handleApiError } from "../../../js/helper/errorHandler";
import EtsyConnectModal from "../components/EtsyConnectModal";

const EtsyAccountList = () => {
  const [etsyAccountList, setEtsyAccountList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showEtsyModal, setShowEtsyModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);

  // Fetch Data
  const fetchAccounts = async () => {
    try {
      setIsLoading(true);
      const response = await EtsyAPI.getAccounts();

      if (response.data.status === 200) {
        setEtsyAccountList(response.data.data.accounts);
      } else {
        handleApiError(response.data, "Failed to Fetch Etsy Accounts");
      }

      setIsLoading(false);
    } catch (error) {
      handleApiError(error, "Failed to Fetch Etsy Accounts");
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  // View Details in Modal
  const handleView = (account) => {
    setSelectedAccount(account);
    setShowModal(true);
  };

  // DELETE ACCOUNT
  const handleDelete = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This will remove the Etsy account permanently.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, remove it",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await EtsyAPI.deleteAccount(id);

          if (response.data.status === 200) {
            toast.success("Account removed successfully");
            fetchAccounts(); // refresh
          } else {
            handleApiError(response.data, "Failed to Delete Etsy Account");
          }
        } catch (error) {
          handleApiError(error, "Failed to Delete Etsy Account");
        }
      }
    });
  };

  // TOGGLE ACTIVE/INACTIVE
  const handleToggle = async (id, currentStatus) => {
    Swal.fire({
      title: "Change Status?",
      text: `Do you want to ${
        currentStatus ? "Deactivate" : "Activate"
      } this account?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await EtsyAPI.toggleAccount(id);

          if (response.data.status === 200) {
            toast.success("Status changed successfully");
            fetchAccounts();
          } else {
            handleApiError(response.data, "Error updating status");
          }
        } catch (error) {
          handleApiError(error, "Error updating status");
        }
      }
    });
  };

  const handleAutoUpdateToggle = async (id, currentStatus) => {
    Swal.fire({
      title: "Change Auto Update Status?",
      text: `Do you want to ${
        currentStatus ? "Disable" : "Enable"
      } auto update for this account?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await EtsyAPI.toggleAutoUpdate(id);

          if (response.data.status === 200) {
            toast.success("Auto update status changed successfully");

            setEtsyAccountList((prev) =>
              prev.map((acc) =>
                acc._id === id
                  ? { ...acc, auto_update_enabled: !currentStatus }
                  : acc,
              ),
            );
          } else {
            handleApiError(response.data, "Error updating auto update status");
          }
        } catch (error) {
          handleApiError(error, "Error updating auto update status");
        }
      }
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSelectedAccount((prevData) => ({ ...prevData, [name]: value }));
  };

  return (
    <>
      <EtsyConnectModal
        show={showEtsyModal}
        onHide={() => setShowEtsyModal(false)}
      />
      <div>
        <Row>
          <Col sm="12">
            <Card>
              <Card.Header className="d-flex justify-content-between">
                <div className="header-title">
                  <h4 className="card-title">Connected Etsy Shop</h4>
                </div>
                <div className="header-title">
                  <Button
                    onClick={() => setShowEtsyModal(true)}
                    className="btn btn-primary"
                  >
                    Connect Etsy Account
                  </Button>
                </div>
              </Card.Header>

              <Card.Body className="px-0" style={{ position: "relative" }}>
                {isLoading && (
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                    }}
                  >
                    <Spinner animation="border" variant="primary" />
                  </div>
                )}

                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr className="ligth">
                        <th>No.</th>
                        <th>Shop</th>
                        <th>Token Expires At</th>
                        <th>Created</th>
                        <th>Status</th>
                        <th>Toggle</th>
                        <th>Auto Update</th>
                        <th min-width="100px">Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {etsyAccountList.map((account, index) => (
                        <tr key={account._id}>
                          <td>{index + 1}</td>
                          <td>{account.shop_name}</td>
                          <td>
                            {new Date(
                              account.token_expires_at,
                            ).toLocaleString()}
                          </td>
                          <td>
                            {new Date(account.createdAt).toLocaleString()}
                          </td>
                          <td>
                            <span
                              className={`badge rounded-pill px-3 py-2 ${
                                account.is_active
                                  ? "bg-success bg-opacity-25 text-success"
                                  : "bg-danger bg-opacity-25 text-danger"
                              }`}
                              style={{ fontSize: "0.85rem", fontWeight: 600 }}
                            >
                              {account.is_active ? "Active" : "Inactive"}
                            </span>
                          </td>

                          <td>
                            <label
                              className="toggle-switch mx-1"
                              title="Activate / Deactivate"
                            >
                              <input
                                type="checkbox"
                                checked={account.is_active}
                                onChange={() =>
                                  handleToggle(account._id, account.is_active)
                                }
                              />
                              <span className="toggle-slider"></span>
                            </label>
                          </td>

                          <td>
                            <label
                              className="toggle-switch mx-1"
                              title="Activate / Deactivate"
                            >
                              <input
                                type="checkbox"
                                checked={!!account.auto_update_enabled}
                                onChange={() =>
                                  handleAutoUpdateToggle(
                                    account._id,
                                    !!account.auto_update_enabled,
                                  )
                                }
                              />
                              <span className="toggle-slider"></span>
                            </label>
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              {/* View */}
                              <button
                                className="btn btn-sm btn-icon btn-info mx-1"
                                onClick={() => handleView(account)}
                                title="View Details"
                              >
                                <i className="ri-eye-line"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-icon btn-danger mx-1"
                                onClick={() => handleDelete(account._id)}
                                title="Delete Account"
                              >
                                <i className="ri-delete-bin-line"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      {/* MODAL FOR DETAILS */}
      {selectedAccount && (
        <Modal
          show={showModal}
          onHide={() => setShowModal(false)}
          centered
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <i className="ri-store-2-line me-2"></i>
              Etsy Account Details
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <div className="new-user-info px-3 py-2">
              <form>
                <div className="row">
                  <div className="mx-auto">
                    <div className="row g-3">
                      {/* SHOP NAME */}
                      <Form.Group className="col-md-6">
                        <Form.Label className="fw-bold">Shop Name</Form.Label>
                        <Form.Control
                          type="text"
                          disabled
                          name="shop_name"
                          placeholder="Enter Etsy Shop Name"
                          value={selectedAccount.shop_name}
                          onChange={handleChange}
                          className="shadow-sm"
                        />
                      </Form.Group>

                      {/* ACCESS TOKEN */}
                      <Form.Group className="col-md-6">
                        <Form.Label className="fw-bold">
                          Access Token
                        </Form.Label>
                        <Form.Control
                          type="text"
                          disabled
                          name="access_token"
                          placeholder="Enter Access Token"
                          value={selectedAccount.access_token}
                          onChange={handleChange}
                          className="shadow-sm"
                        />
                      </Form.Group>

                      {/* REFRESH TOKEN */}
                      <Form.Group className="col-md-6">
                        <Form.Label className="fw-bold">
                          Refresh Token
                        </Form.Label>
                        <Form.Control
                          type="text"
                          disabled
                          name="refresh_token"
                          placeholder="Enter Refresh Token"
                          value={selectedAccount.refresh_token}
                          onChange={handleChange}
                          className="shadow-sm"
                        />
                      </Form.Group>

                      {/* TOKEN EXPIRY */}
                      <Form.Group className="col-md-6">
                        <Form.Label className="fw-bold">
                          Token Expires At
                        </Form.Label>
                        <Form.Control
                          type="datetime-local"
                          disabled
                          name="token_expires_at"
                          value={
                            selectedAccount.token_expires_at
                              ? new Date(selectedAccount.token_expires_at)
                                  .toISOString()
                                  .slice(0, 16)
                              : ""
                          }
                          className="shadow-sm"
                        />
                      </Form.Group>

                      {/* STATUS */}
                      <div className="col-12 mt-3">
                        <p className="fw-bold mb-1">Status:</p>

                        <span
                          className={`badge rounded-pill px-3 py-2 ${
                            selectedAccount.is_active
                              ? "bg-success bg-opacity-25 text-success"
                              : "bg-danger bg-opacity-25 text-danger"
                          }`}
                          style={{ fontSize: "0.9rem", fontWeight: "600" }}
                        >
                          {selectedAccount.is_active ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </Modal.Body>
        </Modal>
      )}

      <footer style={{ marginTop: "300px" }}>
        <div className="footer-body justify-content-center mt-2 mb-2 text-center">
          <p
            style={{ fontSize: "14px", color: "#000", fontWeight: "500" }}
            className="m-0"
          >
            This is a private internal tool used only by the shop owner and
            authorized administrators. This application uses the Etsy API but is
            not endorsed or certified by Etsy.
          </p>
        </div>
      </footer>
    </>
  );
};

export default EtsyAccountList;
